// The Usage screen (REQ-201): the last 30 days' requests, tokens and error rate, its installations'
// health, tokens by day (chart + the same numbers as a table) and usage by model, team or user (a
// line per entity per period — daily, weekly, monthly or yearly, the grain kept in the URL — the
// table and that dimension's share pie). Reads: installations, the usage summary, the day-grain time
// series, and per chosen dimension its `by-<dim>` rows and the time series grouped by it at the
// grain (model.ts); the pie is the table's rows. A model is always shown by its display name, its id
// on hover (primitives/model-names.ts). At the top of the org level, the Billing attribution card
// (SCR-585), which reads API-580 on its own. Under the tiles, the Token classes panel (SCR-584); every
// breakdown and explorer row opens its own class detail, and the by-model table has the Fell back
// column and line (SCR-586). Below them the explorer (TASK-252): org → team → user, one
// level per route (hrefs.ts: `/customers/:orgId/usage[/teams/:teamId[/users/:userId]]` in the
// AllCode admin, `/admin/usage[/…]` on the proxy), its filters and sort in the URL.
//
// The shell that mounts it resolves the `OrgContext` (the org, the surface and the money policy,
// `useOrgContext`) and passes it in; money is shown only as that policy allows (applyMoneyPolicy).
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import { applyMoneyPolicy, type MoneyFields, type MoneyPolicy, type OrgContext } from "../../api/org-context";
import { useHost } from "../../host/host";
import { BarChart } from "../../primitives/chart";
import { errorId, Field } from "../../primitives/field";
import type { HelpId } from "../../primitives/help";
import { LineChart } from "../../primitives/line-chart";
import { Link } from "../../primitives/link";
import { formatPercentBp } from "../../primitives/money";
import { Card, Chip, FilterBar, Segmented, StatCard, StatGrid } from "../../primitives/page";
import { PieChart } from "../../primitives/pie-chart";
import { Empty, ErrorBanner, Loading } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { AttributionCard } from "./attribution-card";
import { LineDetail, TokenClassesPanel } from "./classes";
import { classesUnderPolicy } from "./classes-model";
import { fallbackCount, FallbacksLine } from "./fallbacks-line";
import {
  classLine,
  dailyUsageBars,
  dailyUsageRows,
  dayLabel,
  errorRateBp,
  formatCompactCount,
  formatCount,
  installationsHealthSummary,
  totalTokens,
  usageWindow,
  type DailyUsageRow,
  type Installation,
  type UsageTotals,
} from "./figures";
import { usageDrillPath, type UsageDrill } from "./hrefs";
import { PartialMoney } from "./partial-money";
import {
  comparedDays,
  explorerFilterErrors,
  explorerFiltersFrom,
  explorerNotAvailable,
  explorerSearch,
  fallbacksTitle,
  formatDelta,
  formatPeriod,
  formatPeriodTick,
  GRAIN_OPTIONS,
  grainFrom,
  grainSearch,
  grainWindow,
  KNOWN_PLATFORMS,
  loadDimensionUsage,
  loadExplorer,
  moneyColumns,
  shareSlices,
  sortFrom,
  sortOptions,
  USAGE_DIMENSIONS,
  type DimensionUsage,
  type EntityUsageRow,
  type ExplorerFilters,
  type ExplorerRow,
  type ExplorerView,
  type UsageDimension,
  type UsageGrain,
  type UsageSort,
} from "./model";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

type MoneyRow = MoneyFields & { unpricedModels: readonly string[] };

/**
 * A table's money columns, exactly those the policy shows (moneyColumns): AWS list cost, then
 * Price. `sortable` gives each a compare, an unknown figure sorting below every known one.
 */
function moneyTableColumns<T extends MoneyRow>(policy: MoneyPolicy, sortable = false): Column<T>[] {
  return moneyColumns(policy).map((column) => {
    const field = column === "cost" ? "costMicroUsd" : "priceMicroUsd";
    return {
      key: column,
      ...(column === "cost" ? { header: "AWS list cost", help: "common.awsCost" as const } : { header: "Price", help: "usage.price" as const }),
      numeric: true,
      cell: (row: T) => <PartialMoney micro={row[field]} unpricedModels={row.unpricedModels} />,
      ...(sortable && { compare: (a: T, b: T) => (a[field] ?? -1) - (b[field] ?? -1) }),
    };
  });
}

function dayColumns(policy: MoneyPolicy): Column<DailyUsageRow>[] {
  return [
    { key: "day", header: "Day", cell: (row) => dayLabel(row.day), compare: (a, b) => a.day.localeCompare(b.day) },
    { key: "requests", header: "Requests", numeric: true, cell: (row) => formatCount(row.requests), compare: (a, b) => a.requests - b.requests },
    { key: "tokens", header: "Tokens", numeric: true, cell: (row) => formatCount(row.tokens), compare: (a, b) => a.tokens - b.tokens },
    ...moneyTableColumns<DailyUsageRow>(policy),
  ];
}

const DIMENSION_NOUN: Readonly<Record<UsageDimension, string>> = { model: "Model", team: "Team", user: "User" };

const DIMENSION_HELP = { model: "common.model", team: "usage.byTeam", user: "usage.byUser" } as const satisfies Record<UsageDimension, HelpId>;

/** The table's columns for one dimension: a model's fallbacks under its name, a user's email under theirs, money as the policy shows it. */
function entityColumns(dimension: UsageDimension, policy: MoneyPolicy): Column<EntityUsageRow>[] {
  const columns: Column<EntityUsageRow>[] = [
    {
      key: "entity",
      header: DIMENSION_NOUN[dimension],
      cell: (row) =>
        dimension === "model" ? (
          <>
            <span title={row.title}>{row.label}</span>
            <FallbacksLine fallbacks={row.fallbacks} />
          </>
        ) : (
          <>
            {row.label}
            {row.detail && <span class="cell-sub">{row.detail}</span>}
          </>
        ),
      compare: (a, b) => a.label.localeCompare(b.label),
    },
    { key: "requests", header: "Requests", numeric: true, cell: (row) => formatCount(row.requests), compare: (a, b) => a.requests - b.requests },
    { key: "tokens", header: "Tokens", numeric: true, cell: (row) => formatCount(row.tokens), compare: (a, b) => a.tokens - b.tokens },
    ...moneyTableColumns<EntityUsageRow>(policy, true),
  ];
  if (dimension === "model") {
    columns.splice(2, 0, {
      key: "fallbacks",
      header: "Fell back",
      numeric: true,
      help: "usage.fallbacks",
      cell: (row) => fallbackCount(row.fallbacks),
      compare: (a, b) => (a.fallbacks?.requests ?? 0) - (b.fallbacks?.requests ?? 0),
    });
  }
  return columns;
}

/** A breakdown row's detail: its figures, a model's fallbacks by family, and its token classes (already under the policy, model.ts). */
function EntityDetail({ row, onClose }: { row: EntityUsageRow; onClose(): void }) {
  const facts = [
    ...(row.detail ? [{ label: "Email", value: row.detail }] : []),
    { label: "Requests", value: formatCount(row.requests) },
    { label: "Tokens", value: formatCount(row.tokens) },
    ...(row.fallbacks ? [{ label: "Fell back", value: row.fallbacks.requests > 0 ? `${fallbackCount(row.fallbacks)} (${fallbacksTitle(row.fallbacks)})` : "—" }] : []),
  ];
  return <LineDetail title={row.label} line={row.classes} facts={facts} onClose={onClose} />;
}

export interface UsageTabProps {
  /** The org shown, the surface and the money policy, as the shell resolved them (`useOrgContext`). */
  context: OrgContext;
  drill?: UsageDrill | undefined;
  /** The URL's search: the breakdown's grain and the explorer's filters and sort. */
  query?: URLSearchParams | undefined;
}

export function UsageTab({ context, drill = { level: "org" }, query = new URLSearchParams() }: UsageTabProps) {
  const client = useApi();
  const { orgId, moneyPolicy: policy } = context;
  const [installations, setInstallations] = useState<Installation[] | null>(null);
  const [usage, setUsage] = useState<UsageTotals | null>(null);
  const [days, setDays] = useState<DailyUsageRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [detailError, setDetailError] = useState<ApiError | null>(null);

  const load = () => {
    const { from, to } = usageWindow(new Date());
    setError(null);
    setDetailError(null);
    Promise.all([
      client.get("/v1/orgs/{orgId}/installations", { path: { orgId } }),
      client.get("/v1/orgs/{orgId}/usage/summary", { path: { orgId }, query: { from, to } }),
    ])
      .then(([installationPage, usageSummary]) => {
        setInstallations(installationPage.items);
        setUsage(usageSummary);
      })
      .catch((err: unknown) => setError(unexpected(err)));
    // The same window as the totals (`to` exclusive), so the table adds up to the cards.
    client
      .get("/v1/orgs/{orgId}/usage/timeseries", { path: { orgId }, query: { from, to, grain: "day" } })
      .then((series) => setDays(dailyUsageRows(series.series[0]?.points ?? [], from, to)))
      .catch((err: unknown) => setDetailError(unexpected(err)));
  };

  useEffect(load, [client, orgId]);

  // The card reads on its own, so it shows above the screen's loading and error, and its own
  // failure never takes the screen down (SCR-585).
  const attribution = drill.level === "org" && <AttributionCard orgId={orgId} />;

  if (error)
    return (
      <>
        {attribution}
        <ErrorBanner error={error} onRetry={load} />
      </>
    );
  if (!installations || !usage)
    return (
      <>
        {attribution}
        <Loading label="Loading usage…" />
      </>
    );

  const health = installationsHealthSummary(installations, new Date());
  const bp = errorRateBp(usage);
  // The policy applied once to the totals: the money tiles and the Token classes panel read the same line.
  const money = classesUnderPolicy(policy, classLine(usage));
  const shownDays = days?.map((row) => applyMoneyPolicy(policy, row));
  const allUnpriced = usage.unpricedModels.length > 0 ? "Every model used in the window has no price." : undefined;

  return (
    <>
      {attribution}
      <StatGrid label="Usage over the last 30 days">
        <StatCard label="Requests, 30 days" help="customers.requests" value={formatCount(usage.requests)} />
        <StatCard label="Tokens, 30 days" help="customers.tokens" value={formatCompactCount(totalTokens(usage))} note={`${formatCount(totalTokens(usage))} tokens`} />
        <StatCard label="Error rate" help="customers.errorRate" value={formatPercentBp(bp, 2)} note={`${formatCount(usage.errors)} failed requests`} />
        {policy.list && (
          <StatCard
            label="AWS list cost, 30 days"
            help="common.awsCost"
            value={<PartialMoney micro={money.costMicroUsd} unpricedModels={usage.unpricedModels} />}
            note={money.costMicroUsd === null ? allUnpriced : undefined}
          />
        )}
        {policy.price && (
          <StatCard
            label="Price, 30 days"
            help="usage.price"
            value={<PartialMoney micro={money.priceMicroUsd} unpricedModels={usage.unpricedModels} />}
            note={money.priceMicroUsd === null ? allUnpriced : undefined}
          />
        )}
      </StatGrid>

      <StatGrid label="Installation health">
        <StatCard label="Healthy installations" help="customers.installationHealth" value={health.healthy} />
        <StatCard label="Stale" help="customers.installationHealth" value={health.stale} />
        <StatCard label="No heartbeat yet" help="customers.installationHealth" value={health.unknown} />
        <StatCard label="Revoked" help="customers.installationHealth" value={health.revoked} />
      </StatGrid>

      <TokenClassesPanel line={money} span="the last 30 days" />

      {detailError ? (
        <ErrorBanner error={detailError} onRetry={load} />
      ) : !shownDays ? (
        <Loading label="Loading usage by day…" />
      ) : (
        <>
          <Card title="Tokens by day" help="customers.tokensByDay">
            <div class="split">
              <BarChart
                bars={dailyUsageBars(shownDays)}
                series={[{ id: "tokens", label: "Tokens", class: "series-bar" }]}
                label="Tokens per day over the last 30 days; the table beside it has the same numbers."
                formatTick={formatCompactCount}
                legend={false}
                height={220}
                caption="UTC days. Hover a bar for its exact figures."
              />
              <div class="usage-day-table">
                <DataTable
                  caption="Usage by day"
                  hideCaption
                  columns={dayColumns(policy)}
                  rows={[...shownDays].reverse()}
                  rowKey={(row) => row.day}
                  emptyMessage="No usage in the last 30 days."
                />
              </div>
            </div>
          </Card>
          <UsageBreakdown orgId={orgId} policy={policy} drill={drill} query={query} />
        </>
      )}
      <UsageExplorer context={context} drill={drill} query={query} />
    </>
  );
}

/** What the grain's window covers (grainWindow); the grain's own name is the period noun ("per month"). */
const GRAIN_SPAN: Readonly<Record<UsageGrain, string>> = { day: "the last 30 days", week: "the last 13 weeks", month: "the last 13 months", year: "the last 3 years" };

/**
 * Usage by model, team or user: a line per top entity (tokens per period) above the table, and
 * each entity's share of tokens beside it, named as the table names it. The grain — daily, weekly,
 * monthly, yearly — widens the window with it and lives in the URL, so a link carries it. Switching
 * the dimension or the grain reads that dimension's rows and grouped series; a stale answer for the
 * previous one is dropped.
 */
function UsageBreakdown({ orgId, policy, drill, query }: { orgId: string; policy: MoneyPolicy; drill: UsageDrill; query: URLSearchParams }) {
  const client = useApi();
  const host = useHost();
  const [dimension, setDimension] = useState<UsageDimension>("model");
  const grain = grainFrom(query);
  const [data, setData] = useState<DimensionUsage | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState<EntityUsageRow | null>(null);

  useEffect(() => {
    let current = true;
    setData(null);
    setError(null);
    setOpen(null);
    loadDimensionUsage(client, orgId, dimension, grainWindow(new Date(), grain), grain, policy)
      .then((next) => current && setData(next))
      .catch((err: unknown) => current && setError(unexpected(err)));
    return () => {
      current = false;
    };
  }, [client, orgId, dimension, grain, policy.list, policy.price, attempt]);

  const setGrain = (next: UsageGrain) => host.navigate(usageDrillPath(host, orgId, drill, grainSearch(query, next)));

  const noun = DIMENSION_NOUN[dimension].toLowerCase();
  const span = GRAIN_SPAN[grain];
  const slices = data ? shareSlices(data.rows) : [];
  return (
    <Card
      title={`Usage by ${noun}`}
      titleId="usage-breakdown"
      help={DIMENSION_HELP[dimension]}
      actions={
        <>
          <Field id="usage-grain" label="Chart period" help="usage.grain" compact hideLabel>
            <select id="usage-grain" value={grain} onChange={(event) => setGrain((event.currentTarget as HTMLSelectElement).value as UsageGrain)}>
              {GRAIN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Segmented<UsageDimension> options={USAGE_DIMENSIONS} value={dimension} onChange={setDimension} label="Break usage down by" small />
        </>
      }
    >
      {error ? (
        <ErrorBanner error={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !data ? (
        <Loading label={`Loading usage by ${noun}…`} />
      ) : (
        <div class="stack">
          {data.series.length > 0 && (
            <LineChart
              series={data.series}
              yLabel="Tokens"
              periodNoun={grain}
              label={`Tokens per ${grain} by ${noun} over ${span}: ${data.series.map((s) => s.label).join(", ")}. The table below has the totals.`}
              formatTick={formatCompactCount}
              formatX={(period) => formatPeriod(period, grain)}
              formatXTick={(period) => formatPeriodTick(period, grain)}
              formatValue={formatCount}
            />
          )}
          <div class="split">
            <DataTable
              caption={`Usage by ${noun}, ${span}`}
              hideCaption
              columns={entityColumns(dimension, policy)}
              rows={data.rows}
              rowKey={(row) => row.key}
              emptyMessage={`No usage in ${span}.`}
              onRowClick={setOpen}
              rowLabel={(row) => `Show the token classes of ${row.label}`}
            />
            <section aria-labelledby="usage-share">
              <h3 id="usage-share" class="usage-share-title">
                {DIMENSION_NOUN[dimension]} share of tokens
              </h3>
              {slices.length > 0 ? (
                <PieChart slices={slices} label={`Each ${noun}'s share of tokens over ${span}`} formatValue={formatCompactCount} />
              ) : (
                <Empty message={`No tokens in ${span}.`} />
              )}
            </section>
          </div>
          {open && <EntityDetail row={open} onClose={() => setOpen(null)} />}
        </div>
      )}
    </Card>
  );
}

const NOUN_PLURAL = { team: "teams", user: "users", model: "models" } as const;

/** The explorer's table: the level's rows, money only where the policy shows it, share/delta/quota where the API sends them. */
function explorerColumns(view: ExplorerView, policy: MoneyPolicy): Column<ExplorerRow>[] {
  const columns: Column<ExplorerRow>[] = [
    {
      key: "entity",
      header: view.noun === "team" ? "Team" : view.noun === "user" ? "User" : "Model",
      cell: (row) => (
        <>
          {row.href ? <Link href={row.href}>{row.label}</Link> : <span title={row.title}>{row.label}</span>}
          {row.detail && <span class="cell-sub">{row.detail}</span>}
        </>
      ),
    },
    { key: "tokens", header: "Tokens", numeric: true, cell: (row) => formatCount(row.tokens) },
    { key: "requests", header: "Requests", numeric: true, cell: (row) => formatCount(row.requests) },
    ...moneyTableColumns<ExplorerRow>(policy),
  ];
  if (view.noun === "model") return columns;
  columns.push({ key: "share", header: "Share", numeric: true, help: "usage.share", cell: (row) => formatPercentBp(row.shareBp) });
  if (view.comparedWith) columns.push({ key: "delta", header: "Change", numeric: true, help: "usage.delta", cell: (row) => formatDelta(row.delta) });
  columns.push({
    key: "standing",
    header: "Quota",
    help: "usage.standing",
    cell: (row) => (row.standing ? <Chip tone={row.standing.tone} title={row.standing.title}>{row.standing.label}</Chip> : "—"),
  });
  return columns;
}

/** The explorer's drill-down trail: org → team → user, each but the last a link. */
function ExplorerTrail({ crumbs }: { crumbs: ExplorerView["crumbs"] }) {
  return (
    <nav class="breadcrumb" aria-label="Usage drill-down">
      <ol>
        {crumbs.map((crumb, depth) => (
          <li key={depth}>{crumb.href ? <Link href={crumb.href}>{crumb.label}</Link> : <span aria-current="page">{crumb.label}</span>}</li>
        ))}
      </ol>
    </nav>
  );
}

/** One user's own figures above their models: share of the team, change, quota. */
function SubjectFigures({ subject, policy }: { subject: ExplorerRow; policy: MoneyPolicy }) {
  return (
    <StatGrid label={`${subject.label}'s usage`}>
      <StatCard label="Tokens" help="customers.tokens" value={formatCompactCount(subject.tokens)} note={`${formatCount(subject.tokens)} tokens`} />
      <StatCard label="Requests" help="customers.requests" value={formatCount(subject.requests)} />
      {policy.list && <StatCard label="AWS list cost" help="common.awsCost" value={<PartialMoney micro={subject.costMicroUsd} unpricedModels={subject.unpricedModels} />} />}
      {policy.price && <StatCard label="Price" help="usage.price" value={<PartialMoney micro={subject.priceMicroUsd} unpricedModels={subject.unpricedModels} />} />}
      <StatCard label="Share of the team" help="usage.share" value={formatPercentBp(subject.shareBp)} note={`Change ${formatDelta(subject.delta)}`} />
      <StatCard label="Quota" help="usage.standing" value={subject.standing ? <Chip tone={subject.standing.tone} title={subject.standing.title}>{subject.standing.label}</Chip> : "—"} />
    </StatGrid>
  );
}

/** An explorer row's detail: its figures and its token classes (already under the policy, model.ts). */
function ExplorerDetail({ row, onClose }: { row: ExplorerRow; onClose(): void }) {
  const facts = [
    ...(row.detail ? [{ label: "Email", value: row.detail }] : []),
    { label: "Requests", value: formatCount(row.requests) },
    { label: "Tokens", value: formatCount(row.tokens) },
  ];
  return <LineDetail title={row.label} line={row.classes} facts={facts} onClose={onClose} />;
}

/** The range, model and coding tool, applied on submit (the URL keeps them, so a level link carries them). */
function ExplorerFilterBar({ filters, onApply }: { filters: ExplorerFilters; onApply(filters: ExplorerFilters): void }) {
  const [draft, setDraft] = useState(filters);
  useEffect(() => setDraft(filters), [filters.from, filters.to, filters.model, filters.platform]);
  const errors = explorerFilterErrors(draft);
  const valid = Object.keys(errors).length === 0;
  const edit = (field: keyof ExplorerFilters) => (event: Event) => {
    const value = (event.currentTarget as HTMLInputElement).value;
    setDraft((current) => ({ ...current, [field]: value }));
  };
  return (
    <form
      aria-label="Usage explorer filters"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) onApply({ ...draft, model: draft.model.trim(), platform: draft.platform.trim() });
      }}
    >
      <FilterBar
        end={
          <button type="submit" class="button button-secondary" disabled={!valid}>
            Apply
          </button>
        }
      >
        <Field id="usage-explorer-from" label="From" help="usage.range" compact error={errors.from}>
          <input id="usage-explorer-from" type="date" value={draft.from} onInput={edit("from")} aria-describedby={errors.from ? errorId("usage-explorer-from") : undefined} />
        </Field>
        <Field id="usage-explorer-to" label="To" help="usage.range" compact error={errors.to}>
          <input id="usage-explorer-to" type="date" value={draft.to} onInput={edit("to")} aria-describedby={errors.to ? errorId("usage-explorer-to") : undefined} />
        </Field>
        <Field id="usage-explorer-model" label="Model" help="common.model" compact>
          <input
            id="usage-explorer-model"
            type="search"
            class="input-mono"
            autocomplete="off"
            spellcheck={false}
            placeholder="All models"
            value={draft.model}
            onInput={edit("model")}
          />
        </Field>
        <Field id="usage-explorer-platform" label="Coding tool" help="usage.platform" compact>
          <input
            id="usage-explorer-platform"
            type="search"
            list="usage-explorer-platforms"
            autocomplete="off"
            spellcheck={false}
            placeholder="All tools"
            value={draft.platform}
            onInput={edit("platform")}
          />
          <datalist id="usage-explorer-platforms">
            {KNOWN_PLATFORMS.map((platform) => (
              <option key={platform} value={platform} />
            ))}
          </datalist>
        </Field>
      </FilterBar>
    </form>
  );
}

/**
 * The usage explorer (SCR-232): the org's teams, a team's users, or one user's models, each row's
 * share of the level above and its change on the previous equal-length period, ranked by the
 * API's `sort`. The money policy decides which money columns exist and which sorts are offered;
 * a customer's follows the org's cost visibility, staff see both figures (the shell resolved it).
 */
function UsageExplorer({ context, drill, query }: { context: OrgContext; drill: UsageDrill; query: URLSearchParams }) {
  const client = useApi();
  const host = useHost();
  const { orgId, moneyPolicy: policy } = context;
  const [view, setView] = useState<ExplorerView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState<ExplorerRow | null>(null);

  const filters = explorerFiltersFrom(query, usageWindow(new Date()));
  const sort = sortFrom(query, policy);
  const search = explorerSearch(filters, sort);
  const drillKey = usageDrillPath(host, orgId, drill);

  useEffect(() => {
    let current = true;
    setView(null);
    setError(null);
    setOpen(null);
    loadExplorer(client, { host, orgId, drill, filters, sort, policy })
      .then((next) => current && setView(next))
      .catch((err: unknown) => current && setError(unexpected(err)));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, drillKey, search, policy.list, policy.price, attempt]);

  const go = (nextFilters: ExplorerFilters, nextSort: UsageSort) => host.navigate(usageDrillPath(host, orgId, drill, explorerSearch(nextFilters, nextSort)));

  return (
    <Card
      title="Usage explorer"
      titleId="usage-explorer"
      help="usage.explorer"
      actions={<Segmented<UsageSort> options={sortOptions(policy)} value={sort} onChange={(next) => go(filters, next)} label="Rank rows by" small />}
    >
      <div class="stack">
        {view && <ExplorerTrail crumbs={view.crumbs} />}
        <ExplorerFilterBar filters={filters} onApply={(next) => go(next, sort)} />
        {error && explorerNotAvailable(error) ? (
          <Empty message="The usage explorer is not available yet. The figures above cover the last 30 days." />
        ) : error ? (
          <ErrorBanner error={error} onRetry={() => setAttempt((n) => n + 1)} />
        ) : !view ? (
          <Loading label="Loading the usage explorer…" />
        ) : (
          <>
            {view.subject && <SubjectFigures subject={view.subject} policy={policy} />}
            {view.comparedWith && (
              <p class="muted">
                Change compares with {dayLabel(comparedDays(view.comparedWith).first)} – {dayLabel(comparedDays(view.comparedWith).last)}, the same length just before.
              </p>
            )}
            <DataTable
              caption={`Usage explorer: ${NOUN_PLURAL[view.noun]}, ${dayLabel(filters.from)} – ${dayLabel(filters.to)}`}
              hideCaption
              columns={explorerColumns(view, policy)}
              rows={view.rows}
              rowKey={(row) => row.key}
              emptyMessage={`No usage by any of these ${NOUN_PLURAL[view.noun]} in the range.`}
              csvHref={view.csvHref}
              onRowClick={setOpen}
              rowLabel={(row) => `Show the token classes of ${row.label}`}
            />
            {open && <ExplorerDetail row={open} onClose={() => setOpen(null)} />}
          </>
        )}
      </div>
    </Card>
  );
}
